import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { FileText, User, Building2, Banknote, Loader2, UserPlus, X, Pencil, Users } from 'lucide-react';
import api from '../../utils/axios';

const Field = ({ label, required, children, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <Label className="text-xs font-medium text-slate-600">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
  </div>
);

const EMPTY_PF = { apellido: '', nombre: '', cuit_cdi: '', nro_documento: '', fecha_nacimiento: '', sexo: '', estado_civil: 'SOLTERO', calle: '', nro: '', localidad: '', provincia: '', codigo_postal: '', actividad: '' };
const EMPTY_PJ = { denominacion: '', cuit_cdi: '', calle: '', nro: '', localidad: '', provincia: '', codigo_postal: '', actividad: '' };

const EMPTY = {
  tipo_ros: 'operacion_sospechosa',
  tipo_persona: 'FISICA',
  pf: { ...EMPTY_PF },
  pj: { ...EMPTY_PJ },
  personas: [],
  op: { nro_identificacion: '', monto: '', fecha_inicio: '', fecha_fin: '', producto: 'BANCA ELECTRONICA', moneda: 'Peso Argentino' },
  textos: { descripcion_operatoria: '', analisis_so: '', documentacion_respaldo: '', conclusiones: '' },
  inusualidad_id: '',
};

const personaLabel = (p) => p.tipo_persona === 'JURIDICA'
  ? `${p.denominacion || 'Sin denominación'}`
  : `${p.apellido || ''} ${p.nombre || ''}`.trim() || 'Sin nombre';

export const ROSManualDialog = ({ open, onOpenChange, onCreated, sourceRos }) => {
  const [form, setForm] = useState(EMPTY);
  const [tipos, setTipos] = useState([]);
  const [inusualidades, setInusualidades] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tplSel, setTplSel] = useState('');
  const [saving, setSaving] = useState(false);

  const fromRos = (ros) => {
    const f = ros.formulario_uif || {};
    const op = f.operaciones_productos || {}; const tx = f.textos || {};
    const pfs = f.personas_fisicas || (f.persona_fisica && f.persona_fisica.apellido ? [f.persona_fisica] : []);
    const pjs = f.personas_juridicas || (f.persona_juridica && f.persona_juridica.denominacion ? [f.persona_juridica] : []);
    const personas = [
      ...pfs.map(p => ({ tipo_persona: 'FISICA', ...EMPTY_PF, ...p })),
      ...pjs.map(p => ({ tipo_persona: 'JURIDICA', ...EMPTY_PJ, ...p })),
    ];
    return {
      ...EMPTY,
      tipo_ros: ros.tipo || 'operacion_sospechosa',
      tipo_persona: 'FISICA',
      personas,
      op: { nro_identificacion: op.nro_identificacion || '', monto: op.monto || '', fecha_inicio: op.fecha_inicio || '', fecha_fin: op.fecha_fin || '', producto: op.producto || 'BANCA ELECTRONICA', moneda: op.moneda || 'Peso Argentino' },
      textos: { descripcion_operatoria: tx.descripcion_operatoria || '', analisis_so: tx.analisis_so || '', documentacion_respaldo: tx.documentacion_respaldo || '', conclusiones: tx.conclusiones || '' },
      inusualidad_id: ros.inusualidad_id || '',
    };
  };

  useEffect(() => {
    if (!open) return;
    setTplSel('');
    setForm(sourceRos ? fromRos(sourceRos) : { ...EMPTY, pf: { ...EMPTY_PF }, pj: { ...EMPTY_PJ }, personas: [] });
    api.get('/ros/tipos').then(r => setTipos(r.data || [])).catch(() => {});
    api.get('/ros-config/inusualidad-tipos').then(r => setInusualidades(r.data || [])).catch(() => {});
    api.get('/ros-config/templates').then(r => setTemplates(r.data || [])).catch(() => {});
  }, [open, sourceRos]);

  const setPF = (k, v) => setForm(f => ({ ...f, pf: { ...f.pf, [k]: v } }));
  const setPJ = (k, v) => setForm(f => ({ ...f, pj: { ...f.pj, [k]: v } }));
  const setOP = (k, v) => setForm(f => ({ ...f, op: { ...f.op, [k]: v } }));
  const setTX = (k, v) => setForm(f => ({ ...f, textos: { ...f.textos, [k]: v } }));

  const esPJ = form.tipo_persona === 'JURIDICA';

  // Valida el editor actual y devuelve la persona, o null si está vacío/ inválido (toast).
  const buildEditorPersona = (silent = false) => {
    if (esPJ) {
      const hasData = form.pj.denominacion.trim() || form.pj.cuit_cdi.trim();
      if (!hasData) return null;
      if (!form.pj.denominacion.trim()) { if (!silent) toast.error('Falta la Denominación de la Persona Jurídica'); return false; }
      if (!form.pj.cuit_cdi.trim()) { if (!silent) toast.error('Falta el CUIT de la Persona Jurídica'); return false; }
      return { tipo_persona: 'JURIDICA', ...form.pj };
    }
    const hasData = form.pf.apellido.trim() || form.pf.nombre.trim() || form.pf.cuit_cdi.trim();
    if (!hasData) return null;
    if (!form.pf.apellido.trim() || !form.pf.nombre.trim()) { if (!silent) toast.error('Faltan Apellido y Nombre'); return false; }
    if (!form.pf.cuit_cdi.trim()) { if (!silent) toast.error('Falta el CUIT/CDI'); return false; }
    return { tipo_persona: 'FISICA', ...form.pf };
  };

  const addPersona = () => {
    const p = buildEditorPersona(false);
    if (p === null) return toast.error('Completá los datos de la persona antes de agregarla');
    if (p === false) return;
    setForm(f => ({ ...f, personas: [...f.personas, p], pf: { ...EMPTY_PF }, pj: { ...EMPTY_PJ } }));
    toast.success('Persona agregada al ROS');
  };

  const editPersona = (i) => {
    const p = form.personas[i];
    setForm(f => {
      const personas = f.personas.filter((_, idx) => idx !== i);
      if (p.tipo_persona === 'JURIDICA') return { ...f, personas, tipo_persona: 'JURIDICA', pj: { ...EMPTY_PJ, ...p } };
      return { ...f, personas, tipo_persona: 'FISICA', pf: { ...EMPTY_PF, ...p } };
    });
  };

  const removePersona = (i) => setForm(f => ({ ...f, personas: f.personas.filter((_, idx) => idx !== i) }));

  const applyTemplate = (tplId) => {
    setTplSel(tplId);
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl) return;
    const first = form.personas[0] || buildEditorPersona(true) || {};
    const isPJ = first.tipo_persona === 'JURIDICA';
    const razon = isPJ ? (first.denominacion || '') : `${first.nombre || ''} ${first.apellido || ''}`.trim();
    const cuit = first.cuit_cdi || '';
    const montoTxt = form.op.monto ? `$${Number(form.op.monto).toLocaleString('es-AR')}` : '';
    const vars = {
      nombre: isPJ ? first.denominacion : first.nombre, apellido: first.apellido,
      cuit, cuil: cuit, cuit_cuil: cuit, dni: first.nro_documento, razon_social: razon,
      cvu: form.op.nro_identificacion, monto: montoTxt,
      fecha_inicio: form.op.fecha_inicio, fecha_fin: form.op.fecha_fin,
      tipo_persona: isPJ ? 'Jurídica' : 'Física',
    };
    const repl = (txt) => (txt || '').replace(/\{\s*([\w/]+)\s*\}/g, (m, k) => {
      const key = k.trim().toLowerCase();
      return (key in vars && vars[key] != null && vars[key] !== '') ? String(vars[key]) : m;
    });
    setForm(f => ({ ...f, textos: {
      descripcion_operatoria: repl(tpl.descripcion_operatoria), analisis_so: repl(tpl.analisis_so),
      documentacion_respaldo: repl(tpl.documentacion_respaldo), conclusiones: repl(tpl.conclusiones),
    } }));
    toast.success(`Plantilla "${tpl.nombre}" aplicada`);
  };

  const inusSel = inusualidades.find(i => i.id === form.inusualidad_id);

  const handleSubmit = async () => {
    const personas = [...form.personas];
    const ed = buildEditorPersona(false);
    if (ed === false) return; // editor con datos inválidos
    if (ed) personas.push(ed);
    if (personas.length === 0) return toast.error('Cargá al menos una persona (Física o Jurídica)');
    if (!form.op.nro_identificacion.trim()) return toast.error('Falta el Número de Identificación (CVU)');
    if (!form.textos.descripcion_operatoria.trim() && !form.textos.analisis_so.trim()) {
      return toast.error('Completá la Descripción de la Operatoria o el Análisis del SO');
    }
    setSaving(true);
    try {
      const payload = {
        tipo_ros: form.tipo_ros,
        personas,
        operaciones_productos: { ...form.op, monto: parseFloat(form.op.monto) || 0 },
        textos: form.textos,
        inusualidad_id: form.inusualidad_id || null,
      };
      const res = await api.post('/ros/generar-manual', payload);
      toast.success(`ROS creado con ${personas.length} persona(s)`);
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear el ROS');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="ros-manual-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="text-purple-600" size={20} /> {sourceRos ? 'Duplicar ROS (nuevo, sin caso)' : 'Nuevo ROS Manual (sin caso)'}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Cargá una o varias personas, los datos de la operación y los textos del reporte.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 -mr-4">
          <div className="space-y-6 pb-2">
            <Field label="Tipo de ROS">
              <Select value={form.tipo_ros} onValueChange={v => setForm(f => ({ ...f, tipo_ros: v }))}>
                <SelectTrigger data-testid="tipo-ros-select" className="max-w-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tipos.map(t => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}
                  {tipos.length === 0 && <SelectItem value="operacion_sospechosa">Operación Sospechosa</SelectItem>}
                </SelectContent>
              </Select>
            </Field>

            {/* Personas ya agregadas */}
            {form.personas.length > 0 && (
              <div className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-3" data-testid="personas-list">
                <div className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5 mb-2">
                  <Users size={14} /> Personas en este ROS ({form.personas.length})
                </div>
                <div className="space-y-1.5">
                  {form.personas.map((p, i) => (
                    <div key={i} className="flex items-center justify-between bg-white rounded border border-emerald-200 px-2.5 py-1.5" data-testid={`persona-item-${i}`}>
                      <span className="text-sm flex items-center gap-2 min-w-0">
                        {p.tipo_persona === 'JURIDICA' ? <Building2 size={13} className="text-slate-400" /> : <User size={13} className="text-slate-400" />}
                        <span className="font-medium truncate">{personaLabel(p)}</span>
                        <span className="text-xs text-slate-400">{p.cuit_cdi}</span>
                      </span>
                      <span className="flex items-center gap-1 flex-shrink-0">
                        <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => editPersona(i)} data-testid={`persona-edit-${i}`}><Pencil size={13} /></Button>
                        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-red-500 hover:text-red-700" onClick={() => removePersona(i)} data-testid={`persona-remove-${i}`}><X size={14} /></Button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Editor de persona */}
            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                  {esPJ ? <Building2 size={15} /> : <User size={15} />} {form.personas.length > 0 ? 'Agregar otra persona' : 'Datos del Reportado'}
                </h3>
                <div className="flex gap-2">
                  <Button type="button" variant={!esPJ ? 'default' : 'outline'} className="h-8 text-xs"
                    onClick={() => setForm(f => ({ ...f, tipo_persona: 'FISICA' }))} data-testid="tipo-pf-btn">
                    <User size={13} className="mr-1" /> Física
                  </Button>
                  <Button type="button" variant={esPJ ? 'default' : 'outline'} className="h-8 text-xs"
                    onClick={() => setForm(f => ({ ...f, tipo_persona: 'JURIDICA' }))} data-testid="tipo-pj-btn">
                    <Building2 size={13} className="mr-1" /> Jurídica
                  </Button>
                </div>
              </div>
              {esPJ ? (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Denominación / Razón Social" required><Input value={form.pj.denominacion} onChange={e => setPJ('denominacion', e.target.value)} data-testid="pj-denominacion" /></Field>
                  <Field label="CUIT" required><Input value={form.pj.cuit_cdi} onChange={e => setPJ('cuit_cdi', e.target.value)} placeholder="30-12345678-9" data-testid="pj-cuit" /></Field>
                  <Field label="Calle"><Input value={form.pj.calle} onChange={e => setPJ('calle', e.target.value)} /></Field>
                  <Field label="Nº"><Input value={form.pj.nro} onChange={e => setPJ('nro', e.target.value)} /></Field>
                  <Field label="Localidad"><Input value={form.pj.localidad} onChange={e => setPJ('localidad', e.target.value)} /></Field>
                  <Field label="Provincia"><Input value={form.pj.provincia} onChange={e => setPJ('provincia', e.target.value)} /></Field>
                  <Field label="Código Postal"><Input value={form.pj.codigo_postal} onChange={e => setPJ('codigo_postal', e.target.value)} /></Field>
                  <Field label="Actividad"><Input value={form.pj.actividad} onChange={e => setPJ('actividad', e.target.value)} /></Field>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Apellido" required><Input value={form.pf.apellido} onChange={e => setPF('apellido', e.target.value)} data-testid="pf-apellido" /></Field>
                  <Field label="Nombre" required><Input value={form.pf.nombre} onChange={e => setPF('nombre', e.target.value)} data-testid="pf-nombre" /></Field>
                  <Field label="CUIT / CUIL" required><Input value={form.pf.cuit_cdi} onChange={e => setPF('cuit_cdi', e.target.value)} placeholder="20-12345678-9" data-testid="pf-cuit" /></Field>
                  <Field label="Nº Documento"><Input value={form.pf.nro_documento} onChange={e => setPF('nro_documento', e.target.value)} /></Field>
                  <Field label="Fecha Nacimiento"><Input value={form.pf.fecha_nacimiento} onChange={e => setPF('fecha_nacimiento', e.target.value)} placeholder="dd/mm/aaaa" /></Field>
                  <Field label="Sexo">
                    <Select value={form.pf.sexo || undefined} onValueChange={v => setPF('sexo', v)}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Masculino</SelectItem>
                        <SelectItem value="F">Femenino</SelectItem>
                        <SelectItem value="X">X</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Calle"><Input value={form.pf.calle} onChange={e => setPF('calle', e.target.value)} /></Field>
                  <Field label="Nº"><Input value={form.pf.nro} onChange={e => setPF('nro', e.target.value)} /></Field>
                  <Field label="Localidad"><Input value={form.pf.localidad} onChange={e => setPF('localidad', e.target.value)} /></Field>
                  <Field label="Provincia"><Input value={form.pf.provincia} onChange={e => setPF('provincia', e.target.value)} /></Field>
                  <Field label="Código Postal"><Input value={form.pf.codigo_postal} onChange={e => setPF('codigo_postal', e.target.value)} /></Field>
                  <Field label="Actividad"><Input value={form.pf.actividad} onChange={e => setPF('actividad', e.target.value)} /></Field>
                </div>
              )}
              <Button type="button" variant="outline" className="w-full border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={addPersona} data-testid="add-persona-btn">
                <UserPlus size={15} className="mr-2" /> Agregar otra persona a este ROS
              </Button>
              <p className="text-[11px] text-slate-400 -mt-1">
                Podés cargar varias personas. La que dejes en el formulario también se incluye al crear el ROS.
              </p>
            </div>

            {/* Operaciones y productos */}
            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Banknote size={15} /> Operaciones y Productos</h3>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nº de Identificación (CVU)" required><Input value={form.op.nro_identificacion} onChange={e => setOP('nro_identificacion', e.target.value)} data-testid="op-cvu" /></Field>
                <Field label="Monto (ARS)"><Input type="number" value={form.op.monto} onChange={e => setOP('monto', e.target.value)} data-testid="op-monto" /></Field>
                <Field label="Fecha Inicio"><Input value={form.op.fecha_inicio} onChange={e => setOP('fecha_inicio', e.target.value)} placeholder="dd/mm/aaaa" /></Field>
                <Field label="Fecha Fin"><Input value={form.op.fecha_fin} onChange={e => setOP('fecha_fin', e.target.value)} placeholder="dd/mm/aaaa" /></Field>
                <Field label="Producto"><Input value={form.op.producto} onChange={e => setOP('producto', e.target.value)} /></Field>
                <Field label="Moneda"><Input value={form.op.moneda} onChange={e => setOP('moneda', e.target.value)} /></Field>
              </div>
              {inusualidades.length > 0 && (
                <Field label="Tipo de Inusualidad">
                  <Select value={form.inusualidad_id || undefined} onValueChange={v => setForm(f => ({ ...f, inusualidad_id: v }))}>
                    <SelectTrigger data-testid="op-inusualidad"><SelectValue placeholder="Seleccionar (opcional)" /></SelectTrigger>
                    <SelectContent className="max-w-[600px]">
                      {inusualidades.map(i => (
                        <SelectItem key={i.id} value={i.id}>
                          <span className="whitespace-normal">
                            {i.codigo ? <span className="font-semibold">{i.codigo} — </span> : ''}
                            {i.texto_corto || i.texto_completo}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {inusSel?.texto_completo && (
                    <p className="text-xs text-slate-500 mt-1 italic leading-snug" data-testid="inusualidad-texto-completo">{inusSel.texto_completo}</p>
                  )}
                </Field>
              )}
            </div>

            {/* Textos */}
            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><FileText size={15} /> Textos del Reporte</h3>
              <Field label="Plantilla (autocompleta los textos)" testid="tpl-field">
                {templates.length > 0 ? (
                  <Select value={tplSel || undefined} onValueChange={applyTemplate}>
                    <SelectTrigger data-testid="tpl-select"><SelectValue placeholder="Elegí una plantilla (opcional)" /></SelectTrigger>
                    <SelectContent>
                      {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-xs text-slate-400 italic">No hay plantillas cargadas. Podés crearlas en Configuración de ROS → Plantillas.</p>
                )}
              </Field>
              <Field label="Descripción de la Operatoria" required testid="tx-descripcion">
                <Textarea rows={3} value={form.textos.descripcion_operatoria} onChange={e => setTX('descripcion_operatoria', e.target.value)} />
              </Field>
              <Field label="Análisis del Sujeto Obligado">
                <Textarea rows={3} value={form.textos.analisis_so} onChange={e => setTX('analisis_so', e.target.value)} />
              </Field>
              <Field label="Documentación de Respaldo">
                <Textarea rows={2} value={form.textos.documentacion_respaldo} onChange={e => setTX('documentacion_respaldo', e.target.value)} />
              </Field>
              <Field label="Conclusiones">
                <Textarea rows={2} value={form.textos.conclusiones} onChange={e => setTX('conclusiones', e.target.value)} />
              </Field>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="ros-manual-submit-btn">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileText size={16} className="mr-2" />}
            Crear ROS
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ROSManualDialog;
